import { supabase } from '../lib/supabaseClient';

/**
 * Profile Service
 * Handles user profile updates and contact information management
 */

/**
 * Update user contact information
 * @param {string} userId - User ID (UUID)
 * @param {Object} contactInfo - Contact information to update
 * @param {string} contactInfo.phoneNumber - Phone number
 * @param {string} contactInfo.whatsappNumber - WhatsApp number
 * @param {string} contactInfo.city - City
 * @param {string} contactInfo.state - State (2-char code)
 * @returns {Promise<Object>} Result object with success status
 */
export async function updateContactInfo(userId, { phoneNumber, whatsappNumber, city, state }) {
  try {
    console.log('[profileService] Updating contact info for user:', userId);
    console.log('[profileService] Updates:', { phoneNumber, whatsappNumber, city, state });

    const { data, error } = await supabase.rpc('update_user_contact_info', {
      p_user_id: userId,
      p_phone_number: phoneNumber || null,
      p_whatsapp_number: whatsappNumber || null,
      p_city: city || null,
      p_state: state || null
    });

    if (error) {
      console.error('[profileService] Error updating contact info:', error);
      throw error;
    }

    console.log('[profileService] Contact info updated successfully:', data);

    // Check if the RPC function returned an error in the JSON response
    if (data && !data.success) {
      throw new Error(data.error || 'Failed to update contact information');
    }

    return {
      success: true,
      message: data?.message || 'Contact information updated successfully'
    };
  } catch (error) {
    console.error('[profileService] Exception in updateContactInfo:', error);
    throw error;
  }
}

/**
 * Fetch user profile data
 * @param {string} userId - User ID (UUID)
 * @returns {Promise<Object>} User profile data
 */
export async function fetchUserProfile(userId) {
  try {
    console.log('[profileService] Fetching profile for user:', userId);

    const { data, error } = await supabase
      .from('verified_users')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      console.error('[profileService] Error fetching profile:', error);
      throw error;
    }

    console.log('[profileService] Profile fetched successfully');
    return data;
  } catch (error) {
    console.error('[profileService] Exception in fetchUserProfile:', error);
    throw error;
  }
}

/**
 * Validate phone number format
 * @param {string} phoneNumber - Phone number to validate
 * @returns {boolean} True if valid
 */
export function validatePhoneNumber(phoneNumber) {
  if (!phoneNumber) return true; // Empty is allowed

  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');

  // Check if it's a valid length (10 digits for US, or 11 with country code)
  return digitsOnly.length === 10 || digitsOnly.length === 11;
}

/**
 * Validate state code
 * @param {string} state - State code to validate
 * @returns {boolean} True if valid
 */
export function validateStateCode(state) {
  if (!state) return true; // Empty is allowed
  return state.length === 2;
}

/**
 * Format phone number for display
 * @param {string} phoneNumber - Raw phone number
 * @returns {string} Formatted phone number
 */
export function formatPhoneNumber(phoneNumber) {
  if (!phoneNumber) return '';

  // Remove all non-digit characters
  const digitsOnly = phoneNumber.replace(/\D/g, '');

  // Format as (XXX) XXX-XXXX
  if (digitsOnly.length === 10) {
    return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6)}`;
  }

  // Format with country code: +1 (XXX) XXX-XXXX
  if (digitsOnly.length === 11 && digitsOnly[0] === '1') {
    return `+1 (${digitsOnly.slice(1, 4)}) ${digitsOnly.slice(4, 7)}-${digitsOnly.slice(7)}`;
  }

  // Return as-is if not a standard format
  return phoneNumber;
}
